import { io, type Socket } from 'socket.io-client';

import { MULTIVERSE_SOCKET_URL } from '../const/environments.constants';
import { addDatadogError } from '../plugins/datadog';
import { socketSettings } from './socket.settings';
import { SOCKET_EVENTS } from './socket.constants';
import { fetchSocketToken, SocketTokenAuthError } from './socket-token.api';

let socketInstance: Socket | null = null;

// 401/403 or a rejected token: the session can't recover by retrying, so stop
// reconnecting and report it. Nothing is shown to the user (matches prior behaviour).
const handleSocketAuthError = (reason: string): void => {
    addDatadogError(new Error(`Multiverse socket auth failed: ${reason}`), {
        context: 'multiverse-socket-auth',
        reason,
    });
    if (socketInstance) {
        socketInstance.io.reconnection(false);
        socketInstance.disconnect();
    }
};

export const connectSocket = async (fleetID?: string): Promise<Socket> => {
    if (socketInstance?.connected) {
        return socketInstance;
    }

    const baseUrl = MULTIVERSE_SOCKET_URL;

    socketInstance = io(`${baseUrl}`, {
        ...socketSettings,
        // Function form: re-invoked on every transport open so each (re)connect gets a fresh token.
        auth: async (cb) => {
            try {
                const token = await fetchSocketToken(fleetID);
                cb({ token });
            } catch (error: unknown) {
                if (error instanceof SocketTokenAuthError) {
                    handleSocketAuthError(`TOKEN_FETCH_${error.status}`);
                    return;
                }
                addDatadogError(
                    error instanceof Error ? error : new Error('socket token fetch failed'),
                    { context: 'multiverse-socket-token-fetch' },
                );
                cb({});
            }
        },
    });

    socketInstance.on(SOCKET_EVENTS.CONNECT, () => {
        console.log('[Socket] Connected:', socketInstance?.id);
    });

    socketInstance.on(SOCKET_EVENTS.DISCONNECT, () => {
        console.log('[Socket] Disconnected');
    });

    socketInstance.on(SOCKET_EVENTS.CONNECT_ERROR, (error: Error) => {
        console.error('[Socket] connect_error:', error.message);
        // Other reasons (NO_TOKEN/TOKEN_EXPIRED/JOIN_FAILED/transport) recover on reconnect.
        if (error.message === 'INVALID_TOKEN') {
            handleSocketAuthError('INVALID_TOKEN');
        }
    });

    socketInstance.on(SOCKET_EVENTS.ERROR, (error: Error) => {
        console.error('[Socket] Error:', error);
    });

    socketInstance.on(SOCKET_EVENTS.RECONNECT_FAILED, () => {
        console.error('[Socket] Maximum reconnection attempts reached. Will not try to reconnect.');
    });

    socketInstance.on(SOCKET_EVENTS.RECONNECT_ATTEMPT, (attempt: number) => {
        console.log(`[Socket] Reconnection attempt #${attempt}`);
    });

    return socketInstance;
};

export const disconnectSocket = (): void => {
    if (socketInstance) {
        socketInstance.removeAllListeners();
        socketInstance.disconnect();
        socketInstance = null;
        console.log('[Socket] Disconnected and cleaned up');
    }
};

export const getSocket = (): Socket | null => socketInstance;

export const reconnectSocket = (): void => {
    if (socketInstance && !socketInstance.connected) {
        console.log('[Socket] Force reconnecting...');
        socketInstance.connect();
    }
};

export const emit = (event: string, data?: unknown): void => {
    if (!socketInstance?.connected) {
        console.warn('[Socket] Cannot emit, socket not connected. Attempting to reconnect...');
        reconnectSocket();
        return;
    }
    socketInstance.emit(event, data);
};

export const on = <T extends Record<string, unknown> = Record<string, unknown>>(event: string, callback: (data: T) => void): void => {
    if (!socketInstance) {
        return;
    }
    socketInstance.on(event, callback);
};

export const off = (event: string, callback?: (...args: unknown[]) => void): void => {
    if (!socketInstance) return;
    socketInstance.off(event, callback);
};
